/* eslint-disable no-underscore-dangle */
import React, {
    createContext, useContext, useEffect, useState, useCallback, ReactNode,
} from 'react';
import { RedirectLoginOptions, useAuth0 } from '@auth0/auth0-react';
import { LogoutOptions } from '@auth0/auth0-react/src/auth0-context';
import { setAxiosAccessToken } from 'ns_libs/nsAxios';
import { config } from '../config';

interface Auth0TokenContextValue {
    auth0Loading: boolean;
    isLoading: boolean;
    isAuthenticated: boolean;
    accessToken?: string;
    login: (options?: RedirectLoginOptions) => Promise<void>;
    logout: (options?: LogoutOptions) => Promise<void>;
}

interface Auth0TokenContextProviderProps {
    children: ReactNode;
}

const Auth0TokenContext = createContext<Auth0TokenContextValue | null>(null);

/**
 * Provides Auth0 access token. Automatically refreshes it.
 */
export const useAuth0TokenContext = () => {
    const currentAuth0Context = useContext(Auth0TokenContext);

    if (!currentAuth0Context) {
        throw new Error('useAuth0TokenContext has to be used within <Auth0TokenContext.Provider>');
    }

    return currentAuth0Context;
};

const resetHeapIdentity = () => {
    if ((window as any).heap && typeof (window as any).heap.resetIdentity === 'function') {
        (window as any).heap.resetIdentity();
    }
};

const resetHubspotIdentity = () => {
    if ((window as any)._hsq) {
        (window as any)._hsq.push(['revokeCookieConsent']);
    } else {
        (window as any)._hsq = (window as any)._hsq || [];
        (window as any)._hsq.push(['revokeCookieConsent']);
    }
};

export const Auth0TokenProvider = ({ children }: Auth0TokenContextProviderProps) => {
    const {
        isLoading: auth0Loading,
        isAuthenticated,
        getAccessTokenSilently,
        loginWithRedirect,
        logout: auth0Logout,
        getIdTokenClaims,
    } = useAuth0();

    const refreshIntervalMs = Number(config.auth0AccessTokenRefreshIntervalMinutes) * 60 * 1000;

    const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
    const [isTokenLoading, setIsTokenLoading] = useState<boolean>(false);

    const logout = (options?: LogoutOptions) => {
        resetHubspotIdentity();
        resetHeapIdentity();
        return auth0Logout(options);
    };

    /**
     * Checks if the token has expired beyond the refresh interval by examining
     * the updated_at claim from the ID token.
     */
    const checkTokenExpiration = useCallback(async (): Promise<boolean> => {
        try {
            const claims = await getIdTokenClaims();
            if (!claims?.updated_at) {
                return false;
            }

            const updatedAt = new Date(claims.updated_at);
            const now = new Date();
            const timeSinceUpdate = now.getTime() - updatedAt.getTime();

            return timeSinceUpdate > refreshIntervalMs;
        } catch (error) {
            console.error('Error checking token expiration:', error);
            return false;
        }
    }, [getIdTokenClaims]);

    /**
     * Attempts to silently fetch an access token from Auth0.
     * Uses a timeout safeguard to avoid hanging forever in some browsers.
     * This sets the token in both React state and Axios config.
     */
    const fetchAccessToken = useCallback(async () => {
        setIsTokenLoading(true);
        try {
            /*
       Request access token silently. This method could behave in 3 possible ways:
       1. Open hidden iframe with Auth0 domain with our tenant and request access token using /authorize request. Auth0
          SDK will authorize using cookie from Auth0. It is considered 3rd party cookie, as Auth0 domain is not the same
          as ours. Such approach will work only if 3rd party cookies are allowed for current origin
       2. Use verification page if Auth0 detects that 3rd party cookies are disabled. We have disabled it, so this case
          is not applicable.
       3. If refresh tokens are used, the token endpoint is called directly with the 'refresh_token' grant. We
          do not use refresh tokens.
       4. If we set up custom domain, then getAccessTokenSilently will behave same as #1, but request from the hidden
          iframe will be to origin subdomain, so it will use first party cookie, and it will not be blocked by
          any browser.
      */

            const token = await getAccessTokenSilently({ timeoutInSeconds: 8 });

            if (!token) {
                throw new Error('No token returned from getAccessTokenSilently');
            }

            setAccessToken(token);
            setAxiosAccessToken(token);
        } catch (error) {
            console.error('Failed to get access token silently:', error);

            // Check if token has expired beyond refresh interval before logging out
            // Only logout if both silent refresh failed AND token is significantly expired
            try {
                const isTokenExpired = await checkTokenExpiration();
                if (isTokenExpired) {
                    console.log('Token expired beyond refresh interval and silent refresh failed, logging out...');
                    resetHubspotIdentity();
                    resetHeapIdentity();
                    logout();
                    return;
                }
            } catch (expirationError) {
                console.error('Error checking token expiration:', expirationError);
                // Token expiration check failed; user will remain authenticated.
                console.warn('Token expiration check failed. User remains authenticated due to inability to verify token expiration.');
            }

            // If token expiration check failed or token is not expired, don't logout
            // This prevents premature logouts when user is still authenticated in Core app
            console.warn('Silent token refresh failed but token may still be valid. User remains authenticated.');
        } finally {
            setIsTokenLoading(false);
        }
    }, [getAccessTokenSilently, checkTokenExpiration, logout]);

    /**
     * When the Auth0 SDK is ready, start token fetching:
     * If user is authenticated, fetch the token immediately and on interval
     * If not, skip token fetching and set state as not loading
     */
    useEffect(() => {
        if (!isTokenLoading && !auth0Loading) {
            if (isAuthenticated) {
                fetchAccessToken();

                const intervalId = setInterval(
                    fetchAccessToken,
                    refreshIntervalMs,
                );

                return () => clearInterval(intervalId);
            }
        }
        return undefined;
    }, [isTokenLoading, auth0Loading, isAuthenticated, fetchAccessToken]);

    return (
        <Auth0TokenContext.Provider
            value={{
                auth0Loading,
                isLoading: isTokenLoading,
                isAuthenticated,
                accessToken,
                login: loginWithRedirect,
                logout,
            }}
        >
            {children}
        </Auth0TokenContext.Provider>
    );
};
